/**
 * Participation growth chart component
 * @module components/initiatives/participation-chart
 */

import { useState, useMemo } from 'react';
import { Users } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import {
  getLineChartConfig,
  getThemeColors,
} from '@/lib/utils/chart-config';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { ParticipationGrowth } from '@/types/initiative';
import type { DayOfWeek, MonthName, WeekNumber } from '@/types/common';
import { EmptyState } from '@/components/ui/empty-state';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ParticipationChartProps {
  growth: ParticipationGrowth;
  locale: Locale;
  isDark: boolean;
}

type PeriodType = 'daily' | 'weekly' | 'monthly';

/**
 * Display participation growth chart with daily/weekly/monthly views
 */
export function ParticipationChart({
  growth,
  locale,
  isDark,
}: ParticipationChartProps) {
  const [period, setPeriod] = useState<PeriodType>('monthly');
  const isRTL = locale === 'ar';
  const colors = getThemeColors(isDark);

  const chartData = useMemo(() => {
    let labels: string[];
    let data: number[];

    if (period === 'daily') {
      const days: DayOfWeek[] = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ];
      labels = days.map((day) => t(locale, day.toLowerCase() as any));
      data = days.map((day) => growth.daily[day]);
    } else if (period === 'weekly') {
      const weeks: WeekNumber[] = ['Week_1', 'Week_2', 'Week_3', 'Week_4', 'Week_5'];
      labels = weeks.map((week) =>
        t(locale, week.toLowerCase().replace('_', '') as any)
      );
      data = weeks.map((week) => growth.weekly[week]);
    } else {
      const months: MonthName[] = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      labels = months.map((month) => t(locale, month.toLowerCase() as any));
      data = months.map((month) => growth.monthly[month]);
    }

    return {
      labels,
      datasets: [
        {
          label: t(locale, 'participants'),
          data,
          borderColor: colors.success,
          backgroundColor: colors.success
            .replace(')', ', 0.1)')
            .replace('rgb', 'rgba'),
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [period, growth, locale, colors]);

  const config = getLineChartConfig(isDark, isRTL);
  const options = {
    ...config,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...config.plugins,
      legend: {
        ...config.plugins?.legend,
        display: false,
      },
    },
  };

  // Check if all data is zero
  const hasData = chartData.datasets.some((dataset) =>
    dataset.data.some((value) => value !== 0)
  );

  if (!hasData) {
    return (
      <div className="bg-card p-6 rounded-lg shadow-sm border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">
          {t(locale, 'participationGrowth')}
        </h3>
        <EmptyState
          icon={Users}
          title={t(locale, 'noData')}
          description={t(locale, 'noDataAvailable')}
        />
      </div>
    );
  }

  return (
    <div className="bg-card p-6 rounded-lg shadow-sm border border-border flex flex-col h-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-foreground">
          {t(locale, 'participationGrowth')}
        </h3>
        <div className="flex gap-2" role="tablist" aria-label={t(locale, 'filterBy')}>
          {(['daily', 'weekly', 'monthly'] as PeriodType[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                period === p
                  ? 'bg-primary text-white'
                  : 'bg-muted text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              role="tab"
              aria-selected={period === p}
              aria-label={t(locale, p)}
            >
              {t(locale, p)}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-[350px] relative">
        <div className="absolute inset-0">
          <Line data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
}
